import { useState, useEffect } from 'react';
import {
  UIBox,
  UITypography,
  UICard,
  UICardContent,
  UIButton,
  UIAlertDestructive,
  UIBadge,
  UISkeleton,
} from '@workspace/ui/composed';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@workspace/ui/components/dialog';
import { Trash2, Monitor, Smartphone } from 'lucide-react';
import { api } from '../utils/api-client';
import { formatDistanceToNow } from 'date-fns';
import { useTranslations } from '../i18n/useTranslations';

interface Session {
  id: string;
  ipAddress?: string;
  started?: number;
  lastAccess?: number;
  expires?: number;
  clients?: Array<{ clientId: string; clientName: string }>;
  browser?: string;
  current?: boolean;
}

interface Device {
  os?: string;
  osVersion?: string;
  device?: string;
  lastAccess?: number;
  current?: boolean;
  sessions?: Session[];
  mobile?: boolean;
}

export default function SessionsPage() {
  const { t } = useTranslations('pages.sessions');
  const [loading, setLoading] = useState(true);
  const [devices, setDevices] = useState<Device[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<string | null>(null);
  
  useEffect(() => {
    loadDevices();
  }, []);

  const loadDevices = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.getDevices();
      setDevices(data);
    } catch (err) {
      setError(t('loadError'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSession = async (sessionId: string | null) => {
    try {
      setError(null);
      if (sessionId) {
        await api.deleteSession(sessionId);
      } else {
        await api.deleteAllSessions();
      }
      await loadDevices();
    } catch (err) {
      setError(t('deleteError'));
      console.error(err);
    }
  };

  const openDeleteDialog = (sessionId: string | null) => {
    setSessionToDelete(sessionId);
    setDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    await handleDeleteSession(sessionToDelete);
    setDialogOpen(false);
    setSessionToDelete(null);
  };

  if (loading) {
    return (
      <UIBox className="space-y-6">
        {/* Page Header with Action Button Skeleton */}
        <UIBox className="flex justify-between items-center">
          <UISkeleton className="h-7 w-32" />
          <UISkeleton className="h-9 w-40" />
        </UIBox>

        {/* Sessions List Skeleton */}
        <UICard>
          <UICardContent className="p-6">
            <UIBox className="divide-y divide-border">
              {[1, 2, 3].map((i) => (
                <UIBox key={i} className="flex items-center justify-between py-4 first:pt-0 last:pb-0">
                  <UIBox className="flex items-start gap-4">
                    <UISkeleton className="h-9 w-9 rounded-lg" />
                    <UIBox className="space-y-2">
                      <UISkeleton className="h-5 w-40" />
                      <UISkeleton className="h-4 w-56" />
                      <UISkeleton className="h-3 w-32" />
                    </UIBox>
                  </UIBox>
                  <UISkeleton className="h-8 w-8 rounded" />
                </UIBox>
              ))}
            </UIBox>
          </UICardContent>
        </UICard>
      </UIBox>
    );
  }

  return (
    <UIBox className="space-y-6">
      <UIBox className="flex justify-between items-center">
        <UITypography variant="h4" className="font-semibold">
          {t('title')}
        </UITypography>
        <UIButton
          variant="destructive"
          onClick={() => openDeleteDialog(null)}
          disabled={devices.length === 0}
        >
          {t('signOutAll')}
        </UIButton>
      </UIBox>

      {error && (
        <UIAlertDestructive>
          {error}
        </UIAlertDestructive>
      )}

      <UICard>
        <UICardContent className="p-6">
          {devices.length === 0 ? (
            <UIBox className="flex flex-col items-center justify-center py-12">
              <Monitor className="h-12 w-12 text-muted-foreground mb-4" />
              <UITypography variant="p" className="text-muted-foreground text-center">
                {t('noSessions')}
              </UITypography>
            </UIBox>
          ) : (
            <UIBox className="divide-y divide-border">
              {devices.map((device) =>
                device.sessions?.map((session) => (
                  <UIBox
                    key={session.id}
                    className="flex items-center justify-between py-4 first:pt-0 last:pb-0"
                  >
                    <UIBox className="flex items-start gap-4">
                      <UIBox className="p-2 bg-muted rounded-lg">
                        {device.mobile ? (
                          <Smartphone className="h-5 w-5 text-muted-foreground" />
                        ) : (
                          <Monitor className="h-5 w-5 text-muted-foreground" />
                        )}
                      </UIBox>
                      <UIBox>
                        <UIBox className="flex items-center gap-2">
                          <UITypography variant="p" className="font-medium">
                            {session.browser || t('unknownBrowser')}
                          </UITypography>
                          {session.current && (
                            <UIBadge variant="secondary">
                              {t('current')}
                            </UIBadge>
                          )}
                        </UIBox>
                        <UITypography variant="muted" className="text-sm mt-1">
                          {device.os || t('unknownOS')} {device.osVersion && `${device.osVersion}`} · {session.ipAddress || t('unknownIP')}
                        </UITypography>
                        {session.lastAccess && (
                          <UITypography variant="muted" className="text-sm">
                            {t('lastAccessed')}{' '}
                            {formatDistanceToNow(new Date(session.lastAccess * 1000), {
                              addSuffix: true,
                            })}
                          </UITypography>
                        )}
                      </UIBox>
                    </UIBox>
                    <UIButton
                      variant="ghost"
                      size="icon"
                      onClick={() => openDeleteDialog(session.id)}
                      disabled={session.current}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </UIButton>
                  </UIBox>
                ))
              )}
            </UIBox>
          )}
        </UICardContent>
      </UICard>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('confirmSignOut')}</DialogTitle>
            <DialogDescription>
              {sessionToDelete ? t('confirmSignOutSession') : t('confirmSignOutAll')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <UIButton variant="outline" onClick={() => setDialogOpen(false)}>
              {t('cancel')}
            </UIButton>
            <UIButton variant="destructive" onClick={handleConfirmDelete}>
              {t('signOut')}
            </UIButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </UIBox>
  );
}
